import React, { useRef, useState, useEffect } from 'react';

const TaggingSection = ({
  loadingTags,
  allAvailableTags,
  allCategories,
  selectedTags,
  handleTagToggle,
  proposedTagsList,
  setProposedTagsList,
  handleAddProposedTag,
  pendingChangesData,
}) => {
  const newTagNameRef = useRef(null);
  const newTagCategoryRef = useRef(null);
  const newTagDescriptionRef = useRef(null);
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [pendingTagBeingEdited, setPendingTagBeingEdited] = useState(null);
  const [formShouldStayOpen, setFormShouldStayOpen] = useState(false);

  // Auto-open the new tag form if there are pending proposed tags
  useEffect(() => {
    console.log(
      'TaggingSection useEffect - proposedTagsList:',
      proposedTagsList,
    );
    console.log(
      'TaggingSection useEffect - pendingChangesData:',
      pendingChangesData,
    );

    if (proposedTagsList.length > 0) {
      setFormShouldStayOpen(true);
    }
  }, [proposedTagsList, pendingChangesData]);

  const TagPill = ({ tag, isSelected, onToggle, type = 'approved' }) => {
    let pillClasses = 'profile-tag-pill ';

    if (type === 'approved') {
      pillClasses += 'profile-tag-pill-approved ';
      if (isSelected) {
        pillClasses += 'profile-tag-pill-selected';
      }
    } else {
      pillClasses += 'profile-tag-pill-proposed';
    }

    return (
      <div
        className={pillClasses}
        onClick={() => type === 'approved' && onToggle(tag.id)}
        style={{ cursor: type === 'approved' ? 'pointer' : 'default' }}
      >
        <span className="profile-tag-category">{tag.category}:</span>
        <span className="profile-tag-name">{tag.name}</span>
        {type === 'proposed' && (
          <span className="profile-tag-pending">(pending)</span>
        )}
      </div>
    );
  };

  // Group tags by category for display
  const tagsByCategory = {};
  allAvailableTags.forEach(tag => {
    if (!tagsByCategory[tag.category]) {
      tagsByCategory[tag.category] = [];
    }
    tagsByCategory[tag.category].push(tag);
  });

  const handleAddClick = () => {
    const tagName = newTagNameRef.current?.value?.trim();
    const category = newTagCategoryRef.current?.value || 'Other';
    const description = newTagDescriptionRef.current?.value?.trim() || '';

    if (tagName) {
      if (pendingTagBeingEdited !== null) {
        // Update existing proposed tag
        setProposedTagsList(prev => {
          const newList = [...prev];
          newList[pendingTagBeingEdited] = {
            ...newList[pendingTagBeingEdited],
            name: tagName,
            category: category,
            description: description,
          };
          return newList;
        });
        setPendingTagBeingEdited(null);

        setShowNewTagForm(false);
        setFormShouldStayOpen(false);
      } else {
        // Add new proposed tag
        handleAddProposedTag(tagName, category, description, () => {
          setShowNewTagForm(false);
        });
      }

      // Clear the form
      if (newTagNameRef.current) newTagNameRef.current.value = '';
      if (newTagCategoryRef.current) newTagCategoryRef.current.value = 'Other';
      if (newTagDescriptionRef.current) newTagDescriptionRef.current.value = '';
    }
  };

  const handleToggleNewTagForm = () => {
    const newState = !showNewTagForm;
    setShowNewTagForm(newState);

    // If closing manually, allow it to be closed
    if (!newState) {
      setFormShouldStayOpen(false);
    }
  };

  const handleCloseNewTagForm = () => {
    setShowNewTagForm(false);
    setFormShouldStayOpen(false);
    setPendingTagBeingEdited(null);
    // Clear the form when closing
    if (newTagNameRef.current) newTagNameRef.current.value = '';
    if (newTagCategoryRef.current) newTagCategoryRef.current.value = 'Other';
    if (newTagDescriptionRef.current) newTagDescriptionRef.current.value = '';
  };

  const categoryOptions = allCategories.map(category => (
    <option key={category} value={category}>
      {category}
    </option>
  ));

  return (
    <div className="profile-tagging-container">
      {/* Selected Tags Summary */}
      {selectedTags.size > 0 && (
        <div className="profile-selected-tags-section">
          <div className="profile-selected-tags-title">
            Selected ({selectedTags.size})
          </div>
          <div className="profile-selected-tags-grid">
            {allAvailableTags
              .filter(tag => selectedTags.has(tag.id))
              .map(tag => (
                <TagPill
                  key={tag.id}
                  tag={tag}
                  isSelected={true}
                  onToggle={handleTagToggle}
                />
              ))}
          </div>
        </div>
      )}

      {/* Available Tags by Category */}
      <div className="profile-available-tags-section">
        <div className="profile-available-tags-title">Available Tags</div>

        {Object.keys(tagsByCategory).length === 0 ? (
          <div className="profile-empty-state">No tags available.</div>
        ) : (
          Object.entries(tagsByCategory).map(([category, tags]) => (
            <div key={category} className="profile-category-section">
              <div className="profile-category-title">{category}</div>
              <div className="profile-category-tags">
                {tags.map(tag => (
                  <TagPill
                    key={tag.id}
                    tag={tag}
                    isSelected={selectedTags.has(tag.id)}
                    onToggle={handleTagToggle}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Proposed Tags */}
      {proposedTagsList.length > 0 && (
        <div className="profile-proposed-tags-section">
          <div className="profile-proposed-tags-title">
            Your Proposed Tags (Will be submitted for staff approval)
          </div>
          <div className="profile-proposed-tags-container">
            {proposedTagsList.map((tag, index) => (
              <div
                key={tag.id || `proposed-${index}`}
                className="profile-proposed-tag-item"
              >
                <TagPill
                  tag={tag}
                  isSelected={false}
                  onToggle={() => {}}
                  type="proposed"
                  className="tag"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (tag.name && tag.category) {
                      setPendingTagBeingEdited(index);
                      setShowNewTagForm(true);

                      // Prefill the form with this tag's data
                      setTimeout(() => {
                        if (newTagNameRef.current)
                          newTagNameRef.current.value = tag.name || '';
                        if (newTagCategoryRef.current)
                          newTagCategoryRef.current.value =
                            tag.category ||
                            Object.keys(tagsByCategory)[0] ||
                            '';
                        if (newTagDescriptionRef.current)
                          newTagDescriptionRef.current.value =
                            tag.description || '';
                      }, 0);
                    }
                  }}
                  className="button inline"
                  style={{ marginRight: '5px', marginBottom: '0' }}
                >
                  <div className="align-icon">
                    <i className="fa fa-edit" aria-hidden="true"></i>{' '}
                  </div>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProposedTagsList(prev =>
                      prev.filter((_, i) => i !== index),
                    );
                  }}
                  className="button inline"
                  style={{ marginBottom: '0' }}
                >
                  <div className="align-icon">
                    <i className="fa fa-times-circle" aria-hidden="true"></i>{' '}
                  </div>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toggle button for new tag form */}
      <div className="profile-new-tag-toggle">
        <button
          type="button"
          onClick={handleToggleNewTagForm}
          className="profile-toggle-button button"
        >
          {showNewTagForm ? 'Close Propose Tag Form' : '+ Propose New Tag'}
        </button>
      </div>

      {/* Propose New Tag Section - Auto-opens if there are pending proposed tags */}
      {showNewTagForm && (
        <div className="profile-new-tag-section">
          <div className="profile-new-tag-title">Propose New Tag</div>

          <div className="profile-new-tag-form">
            <div className="profile-input-group profile-input-group-full-width">
              <label className="profile-input-label">Tag Name:</label>
              <input
                ref={newTagNameRef}
                type="text"
                placeholder="Example: Romance, Cars"
                className="profile-text-input"
                maxLength={50}
              />
            </div>

            <div className="profile-input-group">
              <label className="profile-input-label">Category:</label>
              <select ref={newTagCategoryRef} className="profile-select-input">
                {allCategories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="profile-input-group">
              <label className="profile-input-label">
                Description (optional):
              </label>
              <textarea
                ref={newTagDescriptionRef}
                placeholder="Brief description of this tag..."
                className="profile-textarea-input"
                maxLength={200}
              />
            </div>

            <div className="profile-input-group profile-add-button-container">
              <button type="button" onClick={handleAddClick} className="button">
                {pendingTagBeingEdited !== null ? 'Update Tag' : 'Propose Tag'}
              </button>
              {pendingTagBeingEdited !== null && (
                <button
                  type="button"
                  onClick={() => {
                    setPendingTagBeingEdited(null);
                    // Clear the form
                    if (newTagNameRef.current) newTagNameRef.current.value = '';
                    if (newTagCategoryRef.current)
                      newTagCategoryRef.current.value = 'Other';
                    if (newTagDescriptionRef.current)
                      newTagDescriptionRef.current.value = '';
                  }}
                  className="button"
                  style={{ marginLeft: '10px' }}
                >
                  Cancel Edit
                </button>
              )}
              {proposedTagsList.length > 0 && pendingTagBeingEdited === null && (
                <button
                  type="button"
                  onClick={handleCloseNewTagForm}
                  className="button"
                  style={{ marginLeft: '10px' }}
                >
                  Close Form
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaggingSection;
